import { ControlCodes } from '@bcube/shared/constants';
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Plus, Download, Filter, Search, X, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Pagination } from '@/components/ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfigurationLogTable } from '@/components/tables/production/configuration-log-table';
import { configurationLogApi } from '@/api/production/logs';
import { useDebounce } from '@/hooks/use-debounce';
import { usePermissions } from '@/hooks/use-permissions';
import { DateRangeInput } from '@/components/ui/date-range-input';
import type { DateRange } from 'react-day-picker';

export function ConfigurationLogListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(searchParams.get('query') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(ControlCodes.production_tool_configurator);

  // Applied filter states (used in query)
  const [iccid, setIccid] = useState(searchParams.get('iccid') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [profile, setProfile] = useState(searchParams.get('profile') || '');
  const [user, setUser] = useState(searchParams.get('user') || '');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: searchParams.get('from') ? new Date(searchParams.get('from')!) : undefined,
    to: searchParams.get('to') ? new Date(searchParams.get('to')!) : undefined,
  });

  // Temporary filter states (used in modal before applying)
  const [tempIccid, setTempIccid] = useState(iccid);
  const [tempStatus, setTempStatus] = useState(status);
  const [tempProfile, setTempProfile] = useState(profile);
  const [tempUser, setTempUser] = useState(user);

  const page = Number(searchParams.get('page')) || 1;
  const limit = 20;

  // Debounced search to avoid too many API calls (only for main search input)
  const debouncedSearch = useDebounce(searchValue, 300);

  // Sync temporary values with applied values when modal opens
  useEffect(() => {
    if (showFilters) {
      setTempIccid(iccid);
      setTempStatus(status);
      setTempProfile(profile);
      setTempUser(user);
    }
  }, [showFilters, iccid, status, profile, user]);

  const { data: logs, isLoading } = useQuery({
    queryKey: ['configuration-logs', {
      query: debouncedSearch,
      iccid,
      status,
      profile,
      user,
      from: dateRange.from?.toISOString(),
      to: dateRange.to?.toISOString(),
      page
    }],
    queryFn: () =>
      configurationLogApi.findMany({
        query: debouncedSearch || undefined,
        iccid: iccid || undefined,
        status: status || undefined,
        profile: profile || undefined,
        user: user || undefined,
        from: dateRange.from?.toISOString(),
        to: dateRange.to?.toISOString(),
        page,
        limit,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ['configuration-logs-stats'],
    queryFn: () => configurationLogApi.getStats(),
  });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleSearch = () => {
    setIsPending(true);
    // Apply temporary filter values to actual filter state
    setIccid(tempIccid);
    setStatus(tempStatus);
    setProfile(tempProfile);
    setUser(tempUser);

    const newParams = new URLSearchParams();
    if (searchValue) newParams.set('query', searchValue);
    if (tempIccid) newParams.set('iccid', tempIccid);
    if (tempStatus) newParams.set('status', tempStatus);
    if (tempProfile) newParams.set('profile', tempProfile);
    if (tempUser) newParams.set('user', tempUser);
    if (dateRange.from) newParams.set('from', dateRange.from.toISOString());
    if (dateRange.to) newParams.set('to', dateRange.to.toISOString());
    newParams.set('page', '1'); // Reset to first page on search
    setSearchParams(newParams);
    setTimeout(() => setIsPending(false), 300);
  };

  const handleClearFilters = () => {
    setSearchValue('');
    setIccid('');
    setStatus('');
    setProfile('');
    setUser('');
    setTempIccid('');
    setTempStatus('');
    setTempProfile('');
    setTempUser('');
    setDateRange({ from: undefined, to: undefined });
    setSearchParams({});
  };

  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
  };

  const exportMutation = useMutation({
    mutationKey: ['export-configuration-logs'],
    mutationFn: async () => {
      const blob = await configurationLogApi.export({
        query: debouncedSearch || undefined,
        iccid: iccid || undefined,
        status: status || undefined,
        profile: profile || undefined,
        user: user || undefined,
        from: dateRange.from?.toISOString(),
        to: dateRange.to?.toISOString(),
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export.configuration-log.${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });

  const handleExport = () => {
    exportMutation.mutate();
  };

  const hasActiveFilters = iccid || status || profile || user || dateRange.from || dateRange.to;
  const shouldShowResetButton = searchValue || hasActiveFilters;

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Logs de Configuração
          </h1>
          <p className="text-sm text-muted-foreground">
            Visualize e gerencie todos os registros de configuração de dispositivos
          </p>
        </div>

        {canCreate && (
          <Button asChild>
            <Link to="/production/tool/configurator">
              <Plus className="mr-2 h-4 w-4" />
              Nova configuração
            </Link>
          </Button>
        )}
      </div>

      <Separator />

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
            <p className="text-xs text-muted-foreground">logs registrados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bem-sucedidos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.success || 0}</div>
            <p className="text-xs text-muted-foreground">
              configurações com sucesso
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Falhos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.failed || 0}</div>
            <p className="text-xs text-muted-foreground">
              configurações falhas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pendentes de Verificação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(stats as any)?.pending || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              aguardando verificação
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Table Section */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Logs de Configuração</CardTitle>
          <CardDescription>
            Visualize e gerencie todos os registros de configuração
          </CardDescription>

          <div className="w-full flex justify-between items-end pt-3">
            <div className="flex items-center gap-2">
              <Input
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Serial ou nome do usuário"
                className="w-60"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />

              <DateRangeInput
                value={dateRange}
                onChange={setDateRange}
                showTimeInputs
              />

              <Button
                disabled={isPending}
                type="button"
                size="icon"
                variant="outline"
                onClick={() => setShowFilters(true)}
                className="!h-10 !w-10 !min-w-10 flex-shrink-0"
              >
                <Filter className="h-4 w-4" />
              </Button>

              <Button
                disabled={isPending}
                type="submit"
                size="icon"
                variant="outline"
                onClick={handleSearch}
                className="!h-10 !w-10 !min-w-10 flex-shrink-0"
              >
                {isPending ? (
                  <Loader className="animate-spin h-4 w-4" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>

              {shouldShowResetButton && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={handleClearFilters}
                  disabled={isPending}
                  className="!h-10 !w-10 !min-w-10 flex-shrink-0"
                >
                  {isPending ? (
                    <Loader className="animate-spin h-4 w-4" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              disabled={exportMutation.isPending}
              type="button"
              title="Exporte os dados filtrados"
              onClick={handleExport}
              className="ml-4"
            >
              {exportMutation.isPending ? (
                <>
                  <Loader className="animate-spin h-4 w-4 mr-2" />
                  Exportando dados, aguarde...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" /> Exportar
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="w-full text-center py-8">
              <p className="text-gray-500">Carregando...</p>
            </div>
          ) : (
            <>
              <ConfigurationLogTable data={logs?.docs || []} />

              {(logs?.pages ?? 1) > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={logs?.pages ?? 1}
                  totalItems={logs?.total ?? 0}
                  limit={limit}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Filter Dialog */}
      <Dialog open={showFilters} onOpenChange={setShowFilters}>
        <DialogContent className="sm:max-w-[30rem] p-0">
          <DialogHeader className="modal-header">
            <DialogTitle>Filtros adicionais</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 p-6">
            <div className="flex flex-col gap-1">
              <Label htmlFor="iccid">ICCID</Label>
              <Input
                id="iccid"
                value={tempIccid}
                onChange={(e) => setTempIccid(e.target.value)}
                placeholder="Digite o ICCID"
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="status">Status</Label>
              <Select value={tempStatus} onValueChange={setTempStatus}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Sucesso</SelectItem>
                  <SelectItem value="false">Falha</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="profile">Perfil de configuração</Label>
              <Input
                id="profile"
                value={tempProfile}
                onChange={(e) => setTempProfile(e.target.value)}
                placeholder="Nome do perfil"
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="user">Usuário</Label>
              <Input
                id="user"
                value={tempUser}
                onChange={(e) => setTempUser(e.target.value)}
                placeholder="Nome do usuário"
              />
            </div>
          </div>

          <DialogFooter className="p-6 border-t">
            <Button onClick={() => {
              handleSearch();
              setShowFilters(false);
            }} disabled={isPending}>
              {isPending ? (
                <>
                  <Loader className="animate-spin" size={12} /> Filtrando
                </>
              ) : (
                <span>Filtrar</span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
